import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box, Typography, Card, CircularProgress, Table, TableHead,
  TableBody, TableRow, TableCell, Chip, Button, Stack, IconButton,
  Tooltip, Snackbar, Alert,
} from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import EditIcon from "@mui/icons-material/Edit";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { getData, putData, API_ENDPOINTS } from "../../services/apiService";
import ConfigEditDialog from "./ConfigEditDialog";
import { AI_OPS_BG } from "./_aiOpsTheme";

/**
 * Config tab — registry-locked AI tunables editor.
 *
 * Each row shows label / current value / default / category / last
 * update + edit buttons. Edit dialog renders form fields by `type`.
 * Backend busts the cache on every PUT so changes propagate immediately.
 */

const CATEGORY_COLORS = {
  insights: { bg: "#EDE9FE", fg: "#6D28D9" },     // violet
  ask:      { bg: "#DBEAFE", fg: "#1E40AF" },     // indigo / sky
  booking:  { bg: "#CFFAFE", fg: "#0E7490" },     // cyan
  resolver: { bg: "#FEF3C7", fg: "#92400E" },     // amber
};

export default function ConfigTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [toast, setToast] = useState(null);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getData(API_ENDPOINTS.adminInsightsConfig);
      setItems(res?.result?.items || []);
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const submit = async (key, value) => {
    const url = API_ENDPOINTS.adminInsightsConfigUpdate.replace(":key", encodeURIComponent(key));
    try {
      const res = await putData(url, { value });
      setToast({
        kind: "success",
        message: `${key} → ${res?.result?.currentValue ?? value}`,
      });
      setEditing(null);
      fetchConfig();
      return true;
    } catch (err) {
      setToast({
        kind: "error",
        message: err?.response?.data?.message || err.message,
      });
      return false;
    }
  };

  const resetToDefault = (item) => submit(item.key, item.defaultValue);

  // Group rows by category so the table reads as sections.
  const grouped = useMemo(() => {
    const map = new Map();
    for (const it of items) {
      const key = it.category || "other";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(it);
    }
    // Stable category order for display
    const order = ["insights", "ask", "booking", "resolver", "other"];
    return [...map.entries()].sort(
      ([a], [b]) => order.indexOf(a) - order.indexOf(b)
    );
  }, [items]);

  const customCount = useMemo(
    () => items.filter((it) =>
      it.currentValue != null && String(it.currentValue) !== String(it.defaultValue)
    ).length,
    [items]
  );

  if (loading) {
    return <Box sx={{ py: 6, textAlign: "center" }}><CircularProgress /></Box>;
  }
  if (error) {
    return (
      <Card elevation={0} sx={{ border: "1px solid #fca5a5", bgcolor: "#fee2e2", p: 2 }}>
        <Typography color="error">Config failed: {error}</Typography>
      </Card>
    );
  }

  return (
    <Box>
      <Box sx={{ mb:3, p:2, borderRadius:"14px", border:"1px solid #B2DFDF", bgcolor:"#E0F4F5", display:"flex", alignItems:"center", gap:1.5 }}>
        <Box sx={{ width:34,height:34,borderRadius:"9px",background:"linear-gradient(135deg,#007C84,#016268)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
          <InfoOutlinedIcon sx={{ color:"#fff",fontSize:18 }} />
        </Box>
        <Box>
          <Typography sx={{ fontWeight:800, color:"#016268", fontSize:14, fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
            {items.length} tunable{items.length===1?"":"s"}
            {customCount > 0 && <Box component="span" sx={{ ml:1, px:1, py:0.2, bgcolor:"#B2DFDF", borderRadius:"6px", fontSize:12, color:"#007C84", fontWeight:700 }}>{customCount} customised</Box>}
          </Typography>
          <Typography sx={{ fontSize:12, color:"#007C84" }}>
            Edits take effect immediately — backend busts in-memory cache on every save.
          </Typography>
        </Box>
      </Box>

      <Stack spacing={3}>
        {grouped.map(([cat, rows]) => (
          <Card
            key={cat}
            elevation={0}
            sx={{ border:`1px solid ${AI_OPS_BG.cardBorder}`, borderRadius:"16px", overflow:"hidden", mb:0 }}
          >
            <Box sx={{ px:2.5, py:1.5, bgcolor:"#F8FAFC", borderBottom:"1px solid #E2E8F0", display:"flex", alignItems:"center", gap:1.2 }}>
              <CategoryBadge category={cat} />
              <Typography sx={{ fontWeight:800, color:"#1E293B", fontSize:13, fontFamily:"'Plus Jakarta Sans',sans-serif", textTransform:"uppercase", letterSpacing:0.4 }}>
                {cat || "other"}
              </Typography>
              <Box sx={{ ml:"auto", px:1, py:0.2, bgcolor:"#E2E8F0", borderRadius:"6px" }}>
                <Typography sx={{ fontSize:11, fontWeight:700, color:"#64748B" }}>{rows.length} setting{rows.length===1?"":"s"}</Typography>
              </Box>
            </Box>

            <Table size="small">
              <TableHead>
                <TableRow sx={{ "& .MuiTableCell-root": { bgcolor:"#FAFAFC", fontWeight:800, fontSize:11, textTransform:"uppercase", letterSpacing:0.5, color:"#475569", py:1.5, borderBottom:"2px solid #E2E8F0" } }}>
                  <TableCell>Setting</TableCell>
                  <TableCell>Current</TableCell>
                  <TableCell>Default</TableCell>
                  <TableCell>Last updated</TableCell>
                  <TableCell>By</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((it) => {
                  const isDefault = (
                    it.currentValue == null ||
                    String(it.currentValue) === String(it.defaultValue)
                  );
                  return (
                    <TableRow
                      key={it.key}
                      hover
                      sx={{
                        "&:hover": { bgcolor: AI_OPS_BG.rowHover },
                      }}
                    >
                      <TableCell sx={{ verticalAlign: "top", py: 1.5 }}>
                        <Typography fontWeight={700} sx={{ color: "#0F172A" }}>
                          {it.label}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#64748B",
                            fontFamily: "monospace",
                            display: "block",
                            mt: 0.25,
                          }}
                        >
                          {it.key}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: "#475569", mt: 0.75, lineHeight: 1.45, maxWidth: 540 }}
                        >
                          {it.description}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ verticalAlign: "top", py: 1.5 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip
                            label={formatValue(it)}
                            sx={{
                              fontFamily: "monospace",
                              fontWeight: 800,
                              height: 28,
                              ...(isDefault
                                ? { bgcolor: "#F1F5F9", color: "#475569" }
                                : {
                                    background: "linear-gradient(135deg, #E0F4F5, #B2DFDF)",
                                    color: "#007C84",
                                    border: "1px solid #80CCCC",
                                  }),
                            }}
                          />
                          {!isDefault && (
                            <Chip
                              size="small"
                              label="custom"
                              variant="outlined"
                              sx={{
                                fontSize: 10,
                                fontWeight: 700,
                                color: "#007C84",
                                borderColor: "#80CCCC",
                              }}
                            />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ verticalAlign: "top", py: 1.5, fontFamily: "monospace", color: "#64748B" }}>
                        {String(it.defaultValue)}
                      </TableCell>
                      <TableCell sx={{ verticalAlign: "top", py: 1.5, whiteSpace: "nowrap", fontSize: 11, color: "#64748B" }}>
                        {it.updatedAt ? new Date(it.updatedAt).toLocaleString() : "—"}
                      </TableCell>
                      <TableCell sx={{ verticalAlign: "top", py: 1.5, fontSize: 11, color: "#64748B" }}>
                        {it.updatedBy || <span style={{ opacity: 0.6 }}>(seed)</span>}
                      </TableCell>
                      <TableCell sx={{ verticalAlign: "top", py: 1.5 }} align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Tooltip title="Reset to default">
                            <span>
                              <IconButton
                                size="small"
                                disabled={isDefault}
                                onClick={() => resetToDefault(it)}
                              >
                                <RestartAltIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<EditIcon fontSize="small" />}
                            onClick={() => setEditing(it)}
                            sx={{
                              background: "linear-gradient(135deg, #007C84, #016268)",
                              textTransform: "none",
                              fontWeight: 700,
                              boxShadow: "0 3px 8px rgba(0,124,132,0.30)",
                              "&:hover": {
                                background: "linear-gradient(135deg, #016268, #014f54)",
                              },
                            }}
                          >
                            Edit
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        ))}
        {items.length === 0 && (
          <Card elevation={0} sx={{ border: `1px solid ${AI_OPS_BG.cardBorder}`, p: 4, textAlign: "center" }}>
            <Typography color="text.secondary">
              No config keys registered. Run the seed: <code>node seeds/aiAppConfig.seed.js</code>
            </Typography>
          </Card>
        )}
      </Stack>

      <ConfigEditDialog
        item={editing}
        onClose={() => setEditing(null)}
        onSubmit={submit}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={toast?.kind || "success"} onClose={() => setToast(null)} sx={{ fontWeight: 600 }}>
          {toast?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

function CategoryBadge({ category }) {
  const colors = CATEGORY_COLORS[category] || { bg: "#F1F5F9", fg: "#475569" };
  return (
    <Box
      sx={{
        width: 10,
        height: 10,
        borderRadius: "50%",
        background: colors.fg,
        flexShrink: 0,
      }}
    />
  );
}

function formatValue(it) {
  if (it.currentValue == null) return "(unset)";
  if (it.type === "percent") {
    const n = Number(it.currentValue);
    return `${n} (${(n * 100).toFixed(1)}%)`;
  }
  if (it.type === "integer") {
    return Number(it.currentValue).toLocaleString();
  }
  return String(it.currentValue);
}
